import { useRailsContext } from 'components/rails-context'
import * as React from 'react'
import { store } from 'store/index'

export type HealthAuditSettingsFormProps = {
  calledBy: string
  enableHealthAudit: boolean
  showAlertsInTeamManagement: boolean
  healthAuditCheckSettings: Record<string, boolean>
}

const Toggle = ({
  checked,
  onChange,
  disabled = false,
}: {
  checked: boolean
  onChange: () => void
  disabled?: boolean
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-disabled={disabled}
    disabled={disabled}
    onClick={onChange}
    className="relative shrink-0 transition-colors"
    style={{
      width: 26,
      height: 16,
      borderRadius: 10,
      backgroundColor: checked ? '#2E9E5D' : '#E7EAF4',
      opacity: disabled ? 0.5 : 1,
      cursor: disabled ? 'not-allowed' : 'pointer',
    }}
  >
    <span
      className="absolute rounded-full bg-white transition-all"
      style={{
        width: 12,
        height: 12,
        top: 2,
        left: checked ? 12 : 2,
      }}
    />
  </button>
)

type CheckItem = { id: string; label: string }
type Category = { key: string; label: string; items: CheckItem[] }

const CATEGORIES: Category[] = [
  {
    key: 'skill',
    label: 'Skill',
    items: [
      { id: 'skill_missing_description', label: 'Missing description' },
      {
        id: 'skill_no_positions',
        label: 'Skill should be attached to at least one position',
      },
      {
        id: 'skill_level_missing_description',
        label: 'Missing skill level description',
      },
      { id: 'skill_unused', label: 'Not used by any positions' },
      {
        id: 'skill_not_updated',
        label: 'Skill has not been updated for over 6 months',
      },
    ],
  },
  {
    key: 'position',
    label: 'Position',
    items: [
      { id: 'position_no_skills', label: 'No skills' },
      { id: 'position_no_assigned_people', label: 'No assigned people' },
    ],
  },
  {
    key: 'framework',
    label: 'Framework',
    items: [
      {
        id: 'framework_empty_categories',
        label: 'Empty framework categories',
      },
      { id: 'empty_framework', label: 'No skills in framework' },
    ],
  },
  {
    key: 'team',
    label: 'Team',
    items: [
      { id: 'team_position_empty', label: 'No position in team' },
      { id: 'team_skill_empty', label: 'No skills in team' },
      { id: 'team_no_members', label: 'No employees in team' },
    ],
  },
  {
    key: 'organization',
    label: 'Organization',
    items: [
      {
        id: 'org_not_updated',
        label: 'Organization has not been updated for over 6 months',
      },
    ],
  },
]

const ALL_CHECK_IDS = CATEGORIES.flatMap((c) => c.items.map((i) => i.id))

// Per-category master switch, persisted alongside the per-check booleans in
// health_audit_check_settings under its own key (e.g. "skill_category_enabled").
const categoryFlagId = (key: string): string => `${key}_category_enabled`

const snakeToCamel = (s: string): string =>
  s.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase())

const buildInitialChecks = (
  saved: Record<string, boolean> | undefined,
): Record<string, boolean> => {
  const result: Record<string, boolean> = {}
  for (const id of ALL_CHECK_IDS) {
    const savedValue = saved?.[snakeToCamel(id)]
    result[id] = savedValue ?? true
  }
  return result
}

const buildInitialCategoriesEnabled = (
  saved: Record<string, boolean> | undefined,
): Record<string, boolean> => {
  const result: Record<string, boolean> = {}
  for (const c of CATEGORIES) {
    const savedValue = saved?.[snakeToCamel(categoryFlagId(c.key))]
    result[c.key] = savedValue ?? true
  }
  return result
}

const buildInitialExpansion = (): Record<string, boolean> => {
  const result: Record<string, boolean> = {}
  for (const c of CATEGORIES) result[c.key] = true
  return result
}

type CategoryCardProps = {
  category: Category
  checks: Record<string, boolean>
  setChecks: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
  categoryEnabled: boolean
  setCategoryEnabled: (enabled: boolean) => void
  expanded: boolean
  toggleExpanded: () => void
}

const CategoryCard = ({
  category,
  checks,
  setChecks,
  categoryEnabled,
  setCategoryEnabled,
  expanded,
  toggleExpanded,
}: CategoryCardProps) => {
  return (
    <div>
      {/* Header row */}
      <div
        className="flex flex-row items-center justify-between p-4"
        style={{
          borderRadius: expanded ? '8px 8px 0 0' : 8,
          borderTop: '1px solid #E7EAF4',
          borderRight: '1px solid #E7EAF4',
          borderLeft: '1px solid #E7EAF4',
          borderBottom: expanded ? 'none' : '1px solid #E7EAF4',
          background: '#F7F9FC',
        }}
      >
        <button
          type="button"
          onClick={toggleExpanded}
          aria-expanded={expanded}
          className="flex flex-row items-center gap-x-2"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <svg
              width="12"
              height="7"
              viewBox="0 0 12 7"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={`transition-transform ${expanded ? '' : 'rotate-180'}`}
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.21967 0.21967C0.512563 -0.0732233 0.987437 -0.0732233 1.28033 0.21967L5.75 4.68934L10.2197 0.21967C10.5126 -0.0732233 10.9874 -0.0732233 11.2803 0.21967C11.5732 0.512563 11.5732 0.987437 11.2803 1.28033L6.28033 6.28033C5.98744 6.57322 5.51256 6.57322 5.21967 6.28033L0.21967 1.28033C-0.0732233 0.987437 -0.0732233 0.512563 0.21967 0.21967Z"
                fill="#5E6580"
              />
            </svg>
          </span>
          <span className="font-talent text-base font-medium leading-5 text-[#1D264A]">
            {category.label}
          </span>
        </button>
        <Toggle
          checked={categoryEnabled}
          onChange={() => {
            if (!categoryEnabled) {
              setChecks((prev) => {
                const next = { ...prev }
                for (const item of category.items) {
                  next[item.id] = true
                }
                return next
              })
            }
            setCategoryEnabled(!categoryEnabled)
          }}
        />
      </div>
      {/* Content rows */}
      {expanded && (
        <div
          style={{
            borderRadius: '0 0 8px 8px',
            borderRight: '1px solid #E7EAF4',
            borderBottom: '1px solid #E7EAF4',
            borderLeft: '1px solid #E7EAF4',
            background: '#FFF',
          }}
        >
          {category.items.map(({ id, label }, index) => (
            <React.Fragment key={id}>
              {index > 0 && <div className="mx-4 h-px bg-[#E7EAF4]" />}
              <div className="flex flex-row items-center justify-between p-4">
                <span className="font-talent text-base font-normal leading-5 text-[#5E6580]">
                  {label}
                </span>
                <Toggle
                  checked={categoryEnabled && checks[id]}
                  onChange={() => {
                    if (!categoryEnabled) {
                      setChecks((prev) => {
                        const next = { ...prev }
                        for (const item of category.items) {
                          next[item.id] = item.id === id
                        }
                        return next
                      })
                      setCategoryEnabled(true)
                    } else {
                      setChecks((prev) => ({ ...prev, [id]: !prev[id] }))
                    }
                  }}
                />
              </div>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  )
}

export const HealthAuditSettingsForm = (
  props: HealthAuditSettingsFormProps,
) => {
  const {
    calledBy,
    enableHealthAudit: initialEnableHealthAudit,
    showAlertsInTeamManagement: initialShowAlertsInTeamManagement,
    healthAuditCheckSettings: initialChecks,
  } = props

  const [enableHealthAudit, setEnableHealthAudit] = React.useState(
    initialEnableHealthAudit,
  )
  const [showAlertsInTeamManagement, setShowAlertsInTeamManagement] =
    React.useState(initialShowAlertsInTeamManagement)

  const [checks, setChecks] = React.useState<Record<string, boolean>>(() =>
    buildInitialChecks(initialChecks),
  )
  const [categoriesEnabled, setCategoriesEnabled] = React.useState<
    Record<string, boolean>
  >(() => buildInitialCategoriesEnabled(initialChecks))
  const [expansion, setExpansion] = React.useState<Record<string, boolean>>(
    buildInitialExpansion,
  )

  React.useEffect(() => {
    setCategoriesEnabled((prev) => {
      const next = { ...prev }
      let changed = false
      for (const cat of CATEGORIES) {
        if (next[cat.key] && cat.items.every((item) => !checks[item.id])) {
          next[cat.key] = false
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [checks])

  const orgSlug = store.currentUser?.org?.slug
  const { csrfToken } = useRailsContext()

  return (
    <form action={`/orgs/${orgSlug}`} method="post" data-turbo="false">
      <input type="hidden" value="patch" name="_method" />
      <input type="hidden" value={csrfToken} name="authenticity_token" />
      <input
        type="hidden"
        name="called_by"
        value={calledBy}
        autoComplete="off"
      />
      <input
        type="hidden"
        name="org[enable_health_audit]"
        value={enableHealthAudit ? '1' : '0'}
      />
      <input
        type="hidden"
        name="org[show_alerts_in_team_management]"
        value={enableHealthAudit && showAlertsInTeamManagement ? '1' : '0'}
      />
      {CATEGORIES.map((category) => {
        const enabled = categoriesEnabled[category.key]
        return (
          <React.Fragment key={category.key}>
            <input
              type="hidden"
              name={`org[health_audit_check_settings][${categoryFlagId(
                category.key,
              )}]`}
              value={enabled ? '1' : '0'}
            />
            {category.items.map(({ id }) => (
              <input
                key={id}
                type="hidden"
                name={`org[health_audit_check_settings][${id}]`}
                value={enabled && checks[id] ? '1' : '0'}
              />
            ))}
          </React.Fragment>
        )
      })}

      <div className="flex flex-col gap-y-4">
        <div className="flex flex-row items-start justify-between rounded-lg border border-[#E7EAF4] p-4">
          <div className="flex flex-col gap-y-1">
            <span className="font-talent text-base font-medium leading-5 text-[#1D264A]">
              Health Audit
            </span>
            <span className="font-talent text-base font-normal leading-5 text-[#5E6580]">
              Help you keep your framework healthy by surfacing gaps and
              prompting timely updates.
            </span>
          </div>
          <Toggle
            checked={enableHealthAudit}
            onChange={() => {
              setEnableHealthAudit(!enableHealthAudit)
            }}
          />
        </div>

        {enableHealthAudit && (
          <div className="flex flex-row items-start justify-between rounded-lg border border-[#E7EAF4] p-4">
            <div className="flex flex-col gap-y-1">
              <span className="font-talent text-base font-medium leading-5 text-[#1D264A]">
                Show alerts in team management
              </span>
              <span className="font-talent text-base font-normal leading-5 text-[#5E6580]">
                Display alerts that require updates or attention on the Teams
                page.
              </span>
            </div>
            <Toggle
              checked={showAlertsInTeamManagement}
              onChange={() => {
                setShowAlertsInTeamManagement(!showAlertsInTeamManagement)
              }}
            />
          </div>
        )}
      </div>

      {enableHealthAudit && showAlertsInTeamManagement && (
        <div className="mt-10">
          <h2 className="font-talent text-[19px] font-normal leading-6 text-[#5E6580]">
            Advanced settings
          </h2>
          <p className="mt-6 font-talent text-base font-normal leading-5 text-[#1D264A]">
            Choose which alerts you want to see in the health audit results.
          </p>
          <div className="mt-6 flex flex-col gap-y-4">
            {CATEGORIES.map((category) => (
              <CategoryCard
                key={category.key}
                category={category}
                checks={checks}
                setChecks={setChecks}
                categoryEnabled={categoriesEnabled[category.key]}
                setCategoryEnabled={(enabled) =>
                  setCategoriesEnabled((prev) => ({
                    ...prev,
                    [category.key]: enabled,
                  }))
                }
                expanded={expansion[category.key]}
                toggleExpanded={() =>
                  setExpansion((prev) => ({
                    ...prev,
                    [category.key]: !prev[category.key],
                  }))
                }
              />
            ))}
          </div>
        </div>
      )}

      <button
        type="submit"
        className="mt-10 font-talent"
        style={{
          background: '#2E9E5D',
          color: '#fff',
          textAlign: 'center',
          borderRadius: 4,
          fontSize: 16,
          fontWeight: 500,
          lineHeight: '20px',
          border: 'none',
          padding: '10px 20px',
          cursor: 'pointer',
        }}
      >
        Save Settings
      </button>
    </form>
  )
}
